import { BehaviorSubject, forkJoin, Observable, of, zip } from 'rxjs';
import { BlogArticle } from '@shared/models/blog-article.model';
import { BlogArticleAdapter } from '@shared/models/blog-article.model';
import { BlogAuthor, BlogAuthorAdapter } from '@shared/models/blog-author.model';
import { BlogCategory, BlogCategoryAdapter } from '@shared/models/blog-category.model';
import { BlogPageAdapter } from '@shared/models/blog-page.model';
import { catchError, map, pluck, take, tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { GhGlobal } from '@shared/helpers/global-functions';
import { HttpClient, HttpHeaders, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { IMetaTag, MetaTagAttr } from '@core/seo/social-share/meta-tag.interface';
import { Injectable } from '@angular/core';
import { ISocialShare } from '@core/seo/social-share/social-share.interface';
import { JsonLd } from '@core/seo/json-ld/json-ld.service';
import { JsonLdService } from '@core/seo/json-ld/json-ld.service';
import { Location } from '@angular/common';
import { SocialShareService } from '@core/seo/social-share/social-share.service';

const blogBaseUrl = `${environment.apiUrl}${environment.blogMicroService}`;
const locale = environment.locale;

@Injectable({ providedIn: 'root' })
export class BlogService {
  sessionCache: {
    sessionId: string;
    categories: BlogCategory[];
    authors: BlogAuthor[];
    articles: BlogArticle[];
  };
  currentArticleSlug = '';
  articleSearchResult = new BehaviorSubject(null);

  private BLOG_CATEGORY_ENDPOINT = ((slug: string) => `${blogBaseUrl}/blog/category/${locale}/${slug}`);
  private BLOG_CATEGORIES_ENDPOINT = `${blogBaseUrl}/blog/categories/${locale}`;

  private BLOG_AUTHOR_ENDPOINT = ((slug: string) => `${blogBaseUrl}/blog/author/${locale}/${slug}`);

  private BLOG_ARTICLE_ENDPOINT = ((slug: string) => `${blogBaseUrl}/blog/article/${locale}/${slug}`);
  private BLOG_ARTICLES_ENDPOINT = `${blogBaseUrl}/blog/articles/${locale}`;
  private BLOG_ARTICLES_CATEGORY_ENDPOINT = ((categoryId: number) => `${this.BLOG_ARTICLES_ENDPOINT}/category?id=${categoryId}&size=20`);
  private BLOG_ARTICLES_AUTHOR_ENDPOINT = ((authorId: number) => `${this.BLOG_ARTICLES_ENDPOINT}/author?id=${authorId}&size=20`);
  private BLOG_ARTICLES_FEATURED_ENDPOINT = `${this.BLOG_ARTICLES_ENDPOINT}/featured`;
  private BLOG_ARTICLES_POPULAR_ENDPOINT = `${this.BLOG_ARTICLES_ENDPOINT}/popular`;
  private BLOG_ARTICLE_SEARCH_ENDPOINT = `${this.BLOG_ARTICLES_ENDPOINT}/search`; // search by tags
  private BLOG_ARTICLES_TITLES_ENDPOINT = `${this.BLOG_ARTICLES_ENDPOINT}/title`; // search by title

  httpOptions = {
    headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  };

  constructor(
    private http: HttpClient,
    private blogArticleAdapter: BlogArticleAdapter,
    private blogCategoryAdapter: BlogCategoryAdapter,
    private blogAuthorAdapter: BlogAuthorAdapter,
    private blogPageAdapter: BlogPageAdapter,
    private socialShareService: SocialShareService,
    private jsonLdService: JsonLdService,
    protected location: Location,
  ) { }

  /**
   * Return all categories so we can validate the category route
   *
   * @returns category[] = [{id, name, slug, description}]
   */
  getCategories(): Observable<BlogCategory[]> {
    return this.http.get(this.BLOG_CATEGORIES_ENDPOINT)
      .pipe(
        take(1),
        catchError(this.handleError<BlogCategory>('Get Categories')),
        map((res: any) => {
          if (res && !res.error && res.content) {
            const categories = [];
            res.content.forEach((cat: any) => {
              categories.push(this.blogCategoryAdapter.adapt(cat));
            });
            return categories;
          }
          return [];
        })
      );
  }

  getCategoryBySlug(slug: string, region = locale): Observable<BlogCategory> {
    let endpoint = this.BLOG_CATEGORY_ENDPOINT(slug);
    if (slug === '404') {
      return;
    }
    if (region != locale) {
      endpoint = endpoint.replace(locale, region)
    }
    return this.http.get(endpoint)
      .pipe(
        take(1),
        catchError(this.handleError<BlogCategory>('Get Category')),
        map((res: any) => {
          if (res && !res.error) {
            return this.blogCategoryAdapter.adapt(res);
          }
        })
      );
  }

  /**
   * Return all article slugs so we can validate the article route
   *
   * @returns slugs[] = [slug, slug, slug]
   */
  getAllActiveArticleSlugs(): Observable<any> {
    return this.http.get(this.BLOG_ARTICLE_ENDPOINT('all'));
  }

  /**
   * Returns the details of an article
   *
   * @returns article = {id, slug, hero, title, author, categories[category], tags[string], date, description, content}
   */
  getArticleBySlug(slug = this.currentArticleSlug): Observable<any> {
    return this.http.get(this.BLOG_ARTICLE_ENDPOINT(slug))
      .pipe(
        take(1),
        catchError(this.handleError<BlogArticle>('Get Article by Slug')),
        map((res: any) => {
          if (res && !res.error) {
            const article = this.blogArticleAdapter.adapt(res);
            this.setSeoData(article);
            return article;
          }
        })
      );
  }

  getArticlesForCategory(categoryId: number, pageNumber = '0', region: string = locale): Observable<any> {
    let endpoint = this.BLOG_ARTICLES_CATEGORY_ENDPOINT(categoryId) + `&page=${pageNumber}`;
    if (region != locale) {
      endpoint = endpoint.replace(locale, region)
    }
    return this.http.get(endpoint)
      .pipe(
        catchError(this.handleError<BlogArticle>('Get Articles in Category'))
      );
  }

  getArticlesForAuthor(authorId: number, pageNumber = '0', region: string = locale): Observable<any> {
    let endpoint = this.BLOG_ARTICLES_AUTHOR_ENDPOINT(authorId) + `&page=${pageNumber}`;
    if (region != locale) {
      endpoint = endpoint.replace(locale, region)
    }
    return this.http.get(endpoint)
      .pipe(
        catchError(this.handleError<BlogArticle>('Get Articles for Author'))
      );
  }

  getArticlesFeatured(): Observable<any> {
    return this.http.get(this.BLOG_ARTICLES_FEATURED_ENDPOINT)
      .pipe(
        catchError(this.handleError<BlogArticle>('Get Articles Featured'))
      );
  }

  getArticlesPopular(): Observable<any> {
    return this.http.get(this.BLOG_ARTICLES_POPULAR_ENDPOINT)
      .pipe(
        catchError(this.handleError<BlogArticle>('Get Articles Popular'))
      );
  }

  getMainPagedArticles(pageNumber = 0, region: string = locale): Observable<any> {
    let endpoint = this.BLOG_ARTICLES_ENDPOINT + `?terms=not_featured&page=${pageNumber}&size=10`;
    if (region != locale) {
      endpoint = endpoint.replace(locale, region)
    }
    return this.http.get(endpoint).pipe(
      catchError(this.handleError<BlogArticle>('Get Main Paged Articles'))
    );
  }

  findArticlesByTerms(terms: string, pageNumber = '0'): Observable<any> {
    if (!terms) {
      return of([]);
    }
    const searchTerms = terms.split(/\s/g);
    let params = new HttpParams({ encoder: new GhParamsCodec() });
    params = params.append('terms', terms);
    params = params.append('page', pageNumber);
    params = params.append('size', '20');

    return this.http.get(this.BLOG_ARTICLE_SEARCH_ENDPOINT, { params })
      .pipe(
        take(1),
        catchError(this.handleError<BlogArticle>('Get Articles by Tags')),
        map((res: any) => {
          if (res && !res.error) {
            const articles: BlogArticle[] = [];
            const pager = this.blogPageAdapter.adapt(res);
            res.content.forEach((article: any) => {
              article.title = GhGlobal.wrapWithBold(article.title, searchTerms);
              article.summary = GhGlobal.wrapWithBold(article.summary, searchTerms);
              articles.push(this.blogArticleAdapter.adapt(article));
            });
            return { pager, articles };
          }
        })
      );
  }

  findArticlesByTitle(terms: string): Observable<any> {
    let params = new HttpParams({ encoder: new GhParamsCodec() });
    params = params.append('terms', terms);

    return this.http.get(this.BLOG_ARTICLES_TITLES_ENDPOINT, { params })
      .pipe(
        catchError(this.handleError<BlogArticle>('Get Articles by Title'))
      );
  }

  addPopularity(slug: string): Observable<any> {
    if (!slug) {
      return;
    }
    return this.http.put(this.BLOG_ARTICLE_ENDPOINT(slug), '');
  }

  checkSlug(): Observable<any> {
    const categorySlugs$ = this.getCategories();
    const articleSlugs$ = this.getAllActiveArticleSlugs();
    return forkJoin([categorySlugs$, articleSlugs$]);
  }

  resetSessionCache() {
    this.sessionCache = {
      sessionId: '',
      articles: undefined,
      authors: undefined,
      categories: undefined
    };
  }

  getAuthorBySlug(slug: string, region: string = locale): Observable<any> {
    let endpoint = this.BLOG_AUTHOR_ENDPOINT(slug);
    if (region != locale) {
      endpoint = endpoint.replace(locale, region)
    }
    return this.http.get(endpoint)
      .pipe(
        take(1),
        catchError(this.handleError<BlogAuthor>('Get Author by Slug')),
        map((res: any) => {
          if (res && !res.error) {
            return this.blogAuthorAdapter.adapt(res);
          }
        })
      );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error);
      // TODO: better job of transforming error for user consumption
      console.log(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }

  private setSeoData(article: BlogArticle) {
    const permaLink = environment.location + '/' + article.category.slug + '/' + article.slug;
    const goTags = article.tags ? article.tags.split(' ').join() : 'gohenry';
    const tags: IMetaTag = { attr: MetaTagAttr.property, attrValue: 'article:tag', value: goTags };

    const seoData: ISocialShare = {
      title: `${article.title} | GoHenry`,
      keywords: `gohenry,${goTags},article`,
      description: article.description,
      image: article.heroImageUrl,
      url: permaLink,
      type: 'article',
      author: article.author.fullName,
      section: article.category.name,
      published: article.publishDate,
      modified: article.modified,
      linkedArticleUrl: article.linkedArticleUrl,
      tags
    };
    const authorLd: JsonLd = this.jsonLdService.getObject('Person', {
      name: article.author.fullName
    });
    const publisherLd: JsonLd = this.jsonLdService.getObject('Organization', {
      url: environment.website,
      logo: environment.jsonImage,
      legalName: 'GoHenry',
      name: 'GoHenry'
    });

    const jsonLd: JsonLd = {
      name: `${article.title} | GoHenry`,
      url: seoData.url,
      author: authorLd,
      publisher: publisherLd,
      headline: article.title,
      description: article.description,
      image: article.heroImageUrl,
      dateCreated: article.publishDate,
      datePublished: article.publishDate,
      dateModified: article.modified
    };

    const jsonLdObject = this.jsonLdService.getObject('BlogPosting', jsonLd);

    this.socialShareService.setData(seoData);
    this.jsonLdService.setData(jsonLdObject);
  }

}

class GhParamsCodec implements HttpParameterCodec {

  encodeKey(k: string): string {
    return encodeURIComponent(k)
      .replace(/\+/gi, '%2B')
      .replace('/\@/gi', '%40');
  }

  encodeValue(v: string): string {
    return encodeURIComponent(v)
      .replace(/\+/gi, '%2B')
      .replace('/\@/gi', '%40');
  }

  decodeKey(k: string): string {
    return decodeURIComponent(k);
  }

  decodeValue(v: string) {
    return decodeURIComponent(v);
  }

}
